import { FC, ReactElement } from 'react';
import {
	Controller,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, Typography } from '@mui/material';

import { LoginFields } from '@mopla/constants';

import { DriverPasswordInput } from '../../atoms/DriverPasswordInput';
import { Input } from '../../atoms/input/input';
import { MplButton } from '../../atoms/MplButton/MplButton';
import { ArrowRightIcon } from '../../icons/ArrowRight';
import { AttentionIcon } from '../../icons/Attention';
import { TSocialLoginMethod } from '../SocialLogin/types';

import { DriverSocialLogin } from './DriverSocialLogin';
import ErrorValidationMessage from './ErrorValidationMessage';
import LoginValidationSchema from './FormValidation';
import {
	ForgotPasswordButton,
	LoginMethodDivider,
	Root,
	SubmitLevel,
} from './styled';

export interface IFormInput {
	[LoginFields.email]: string;
	[LoginFields.password]: string;
}

export interface IDriverLoginFormProps {
	onLogin: SubmitHandler<IFormInput>;
	authError: boolean;
	errorMessage: string | null;
	validateUser: () => Promise<boolean>;
	socialLoginMethods: TSocialLoginMethod[];
	titleComp?: ReactElement | null;
	'data-testid'?: string;
	driverApp?: boolean;
	browser?: boolean;
}

export const DriverLoginForm: FC<IDriverLoginFormProps> = (props) => {
	const {
		onLogin,
		authError,
		errorMessage,
		titleComp = null,
		driverApp,
		browser,
		validateUser,
		socialLoginMethods,
	} = props;
	const navigate = useNavigate();
	const { t } = useTranslation(['logIn', 'validation']);

	const methods = useForm<IFormInput>({
		defaultValues: {
			[LoginFields.email]: '',
			[LoginFields.password]: '',
		},
		resolver: yupResolver(LoginValidationSchema),
		mode: 'all',
	});
	const { formState, handleSubmit } = methods;

	const getHelperText = (key: 'password') => {
		if (formState.errors[key]?.message) {
			return (
				<ErrorValidationMessage
					message={t(`validation:${formState.errors[key].message}`)}
				/>
			);
		}

		if (authError) {
			return <ErrorValidationMessage message={t('validation:auth.error')} />;
		}

		if (errorMessage) {
			return <ErrorValidationMessage message={errorMessage} />;
		}

		return '';
	};

	return (
		<Stack direction="column" gap={3}>
			<FormProvider {...methods}>
				<form
					data-testid={props['data-testid']}
					style={!browser ? { height: '100%' } : {}}
					onSubmit={handleSubmit(onLogin)}
				>
					<Root>
						{titleComp}
						<Controller
							name="email"
							render={({ field }) => (
								<Input
									{...field}
									onChange={(e) => {
										e.target.value = e.target.value.trim();
										field.onChange(e);
									}}
									label={t('label.email')}
									ref={null}
									helperText={
										Boolean(formState.errors.email?.message) && (
											<>
												<AttentionIcon width={15} height={15} />
												<span>
													{t(`validation:${formState.errors.email?.message}`)}
												</span>
											</>
										)
									}
									error={Boolean(formState.errors.email?.message) || authError}
									inputProps={{
										'data-testid': 'loginForm-email-textField',
									}}
									type="email"
									autoComplete="username email"
								/>
							)}
						/>
						<Controller
							name="password"
							render={({ field }) => (
								<DriverPasswordInput
									{...field}
									onChange={(e) => {
										e.target.value = e.target.value.trim();
										field.onChange(e);
									}}
									label={t('label.password')}
									ref={null}
									helperText={getHelperText('password')}
									error={Boolean(
										formState.errors.password?.message || authError
									)}
									inputProps={{
										'data-testid': 'loginForm-password-textField',
									}}
									autoComplete="current-password"
								/>
							)}
						/>

						<ForgotPasswordButton
							variant="text"
							onClick={() =>
								navigate({ pathname: '/welcome', search: 'forgotPassword' })
							}
						>
							{t('text.forgot')}
						</ForgotPasswordButton>
						<SubmitLevel browser={browser}>
							<MplButton
								fullWidth
								color={driverApp ? 'secondary' : 'primary'}
								endIcon={<ArrowRightIcon />}
								data-testid="loginForm-login-button"
								disabled={!formState.isValid}
								type="submit"
							>
								{t('button.logIn')}
							</MplButton>
						</SubmitLevel>
					</Root>
				</form>
			</FormProvider>
			<LoginMethodDivider>
				<Typography variant="subtitle2" mx={3}>
					ODER MIT
				</Typography>
			</LoginMethodDivider>
			<DriverSocialLogin
				validateUser={validateUser}
				socialLoginMethods={socialLoginMethods}
			/>
		</Stack>
	);
};
